import { useCallback, useMemo } from 'react';
import { SearchInput } from 'stubben-edge-unified-frontend-react-components';
import type { SelectOptionProp } from '@ui/SelectField/types';
import type {
  DataTableProviderFilterItem,
  DataTableProviderFilterStateValue
} from './types';

type DatatableFilterSearchInputProps = {
  filter: DataTableProviderFilterItem;
  filtersState: Map<string, DataTableProviderFilterStateValue>;
  label: string;
  value: string | number | '';
  disabled?: boolean;
  testIdentifier: string;
  onChange: (
    value: string | number | string[],
    option?: SelectOptionProp
  ) => void;
};

export function DatatableFilterSearchInput({
  filter,
  filtersState,
  label,
  value,
  disabled = false,
  testIdentifier,
  onChange
}: DatatableFilterSearchInputProps) {
  const autoCompleteOptions = useMemo(
    () =>
      filter.autoCompleteOptions !== undefined
        ? filter.autoCompleteOptions(filtersState)
        : {},
    [filter, filtersState]
  );

  const storedLabel = filtersState.get(filter.filterKey)?.label;
  const displayValue =
    storedLabel !== undefined && storedLabel !== null && storedLabel !== ''
      ? String(storedLabel)
      : '';

  const dependencyKey = useMemo(
    () => JSON.stringify(autoCompleteOptions.resolverDependencies ?? []),
    [autoCompleteOptions.resolverDependencies]
  );

  const resolver = useCallback(
    async (searchTerm: string) => {
      if (autoCompleteOptions.options) {
        let opts = [...autoCompleteOptions.options];
        if (searchTerm) {
          const term = searchTerm.toLowerCase();
          opts = opts.filter(
            (o) =>
              o.label.toLowerCase().includes(term) ||
              `${o.value}`.toLowerCase().includes(term)
          );
        }
        return opts;
      }

      if (!autoCompleteOptions.resolver) {
        return [];
      }

      const result = await autoCompleteOptions.resolver(searchTerm, 1, 20);
      return result ?? [];
    },
    [autoCompleteOptions.options, autoCompleteOptions.resolver]
  );

  const handleSelectOption = useCallback(
    async (selectedValue: string | number) => {
      let option: SelectOptionProp | undefined;

      if (autoCompleteOptions.options) {
        option = autoCompleteOptions.options.find(
          (o) => `${o.value}` === `${selectedValue}`
        );
      }

      if (!option && autoCompleteOptions.itemResolver) {
        try {
          option = await autoCompleteOptions.itemResolver(selectedValue);
        } catch {
          // Keep fallback label below when lookup fails.
        }
      }

      if (!option) {
        option = { value: selectedValue, label: String(selectedValue) };
      }

      onChange(selectedValue, option);
    },
    [autoCompleteOptions, onChange]
  );

  return (
    <SearchInput
      key={`${filter.filterKey}-${dependencyKey}`}
      variant="secondary"
      label={label}
      placeholder={
        autoCompleteOptions.placeholder || filter.placeholder || filter.label
      }
      value={displayValue}
      selectedOption={value === '' ? undefined : value}
      disabled={disabled}
      onSelectOption={handleSelectOption}
      onDeselectOption={() => onChange('', undefined)}
      resolver={resolver}
      noResultsText="No results found"
      testIdentifier={testIdentifier}
    />
  );
}
